import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Listing, LISTING_STAGE } from 'models/Listing';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import ListingContext from '../../../../providers/Listing/Listing.context';
import { useGenerateDropdownOptions } from './hooks/useGenerateDropdownOptions';
import LeadContext from '../../../../../../../Buyer/providers/Lead/Lead.context';
import PencilIcon from 'icons/Pencil.icon';
import utils, {
  checkHasListing,
  checkCanPaused,
  getCreateListingRoute,
  checkIsPublished,
  checkIsArchivedOrSold,
  checkIsDraft,
  checkIsNoPackage,
  checkIsPublishedOrPrivateDrafted,
} from 'utils';
import LinkToIcon from 'icons/LinkTo.icon';
import PlusIcon from 'icons/Plus.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { ConditionalLink } from 'components/ConditionalLink';
import { BUYING_PATH } from 'data/global';
import ChangeListingTierModal from 'components/ChangeListingTierModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';

import './ListingInformationHeader.styles.scss';

const BUTTON_CLASS = 'ncoded-button--outline as-button';

type ListingInformationHeaderProps = {
  className?: string;
  text: string;
};

const ListingInformationHeader: React.FC<ListingInformationHeaderProps> = ({
  className,
  text,
}) => {
  const location = useLocation();

  const { t } = useTranslation();

  const [changeTierModal, changeTierModalRef] = useCallbackRef<OverlayRef>();

  const {
    listing: listingSeller,
    setListing: updateListingSeller,
    dealRoom,
    canManageDeal,
    setDealRoom,
  } = useContext(ListingContext);
  const { listing: listingBuyer } = useContext(LeadContext);

  const isBuying = location.pathname.includes(BUYING_PATH);

  const listing = isBuying ? listingBuyer : listingSeller;
  const {
    industry,
    listingPackage,
    type,
    slugName,
    _id: listingId,
  } = listing || {};
  const {
    stage,
    listingStage,
    wantToBePrivate,
    _id: dealRoomId,
  } = dealRoom || {};

  const isArchivedOrSold = checkIsArchivedOrSold(stage);
  const hasListing = checkHasListing(listingStage);
  const isPublished = checkIsPublished(listingStage);
  const isPublishedOrPrivateDrafted = checkIsPublishedOrPrivateDrafted(
    listingStage,
    wantToBePrivate,
  );
  const isListingNoPackage = checkIsNoPackage(listingPackage);
  const isDraftListing = checkIsDraft(listingStage);

  const canPause = useMemo(
    () => canManageDeal && !isListingNoPackage && checkCanPaused(listingStage),
    [canManageDeal, isListingNoPackage, listingStage],
  );

  const onChangeTier = useCallback(
    () => changeTierModal.open(),
    [changeTierModal],
  );

  const { menuOptions } = useGenerateDropdownOptions({
    listing,
    canPause,
    isArchivedOrSold,
    canDeleteDraft: isDraftListing && !wantToBePrivate,
    hasListing: hasListing && !isListingNoPackage,
    onChangeTier,
    handleClearDraft: (listing: Listing) => {
      updateListingSeller(listing);
      setDealRoom((prev) => ({
        ...prev,
        listing: { ...listing, stage: LISTING_STAGE.NO_LISTING },
        listingStage: LISTING_STAGE.NO_LISTING,
      }));
    },
    handlePauseListing: (dealRoomStatus) => {
      setDealRoom((prev) => ({
        ...prev,
        listing: { ...prev.listing, stage: LISTING_STAGE.PAUSED },
        listingStage: LISTING_STAGE.PAUSED,
        stage: dealRoomStatus,
      }));
    },
  });

  const getLabel = useCallback(
    (Icon, label) => (
      <>
        {t(label)}
        <Icon />
      </>
    ),
    [t],
  );
  const updateListingContent = useMemo(
    () =>
      isPublishedOrPrivateDrafted
        ? getLabel(PencilIcon, 'editListing')
        : isDraftListing
        ? getLabel(ArrowrightIcon, 'completeListing')
        : getLabel(PlusIcon, 'createListing'),
    [isPublishedOrPrivateDrafted, isDraftListing, getLabel],
  );

  return (
    <header className={classNames('listing-information-header', className)}>
      <div>
        <div className="listing-information-header__text">
          <p>{text}</p>
        </div>

        <div className="listing-information-header__actions-menu">
          {!isBuying && (
            <ConditionalLink
              isDisabled={!canManageDeal}
              disabledClassName={BUTTON_CLASS}
              to={getCreateListingRoute(
                dealRoomId,
                listingId,
                type,
                isPublished,
              )}
            >
              {updateListingContent}
            </ConditionalLink>
          )}

          {isPublished && !isBuying && (
            <Link
              to={utils.composeBusinessUrl(industry, slugName, type)}
              className={BUTTON_CLASS}
            >
              <LinkToIcon />
              {t('seeBuyersView')}
            </Link>
          )}

          {!isBuying && canManageDeal && !!menuOptions?.length && (
            <>
              <ThreeDotsDropdown options={menuOptions} />
              <ChangeListingTierModal
                ref={changeTierModalRef}
                listing={listingSeller}
                onChange={updateListingSeller}
              />
            </>
          )}
        </div>
      </div>
    </header>
  );
};
export default ListingInformationHeader;
